import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Alert,
    TextField,
} from "@mui/material";
import { useState } from "react";
import { PrimaryButton } from "@/components/general/buttons/PrimaryButton";
import { handleCheckout } from "./functions";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PixIcon from "@mui/icons-material/Pix";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export type PaymentMethod =
    | "PIX"
    | "CREDIT_CARD"
    | "DEBIT_CARD"
    | "CREDIT_CARD_EARLY_BUYER"
    | "BANK_SLIP";

interface PaymentOption {
    value: PaymentMethod;
    label: string;
    description: string;
    icon: React.ReactNode;
}

const PAYMENT_OPTIONS: PaymentOption[] = [
    {
        value: "PIX",
        label: "PIX",
        description: "Aprovação imediata",
        icon: <PixIcon sx={{ fontSize: 22 }} />,
    },
    {
        value: "CREDIT_CARD",
        label: "Cartão de crédito",
        description: "Parcelamento disponível",
        icon: <CreditCardIcon sx={{ fontSize: 22 }} />,
    },
    {
        value: "BANK_SLIP",
        label: "Boleto bancário",
        description: "Vence em 3 dias úteis",
        icon: <ReceiptLongIcon sx={{ fontSize: 22 }} />,
    },
];

interface CheckoutDialogProps {
    open: boolean;
    cartId: string;
    total: number;
    onClose: () => void;
    onSuccess: () => void;
}

const CheckoutDialog = ({
                            open,
                            cartId,
                            total,
                            onClose,
                            onSuccess,
                        }: CheckoutDialogProps) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [selectedMethod, setSelectedMethod] =
        useState<PaymentMethod>("PIX");

    /**
     * PIX
     */
    const [pixCode, setPixCode] = useState<string | null>(null);

    const [pixQrCodeImage, setPixQrCodeImage] =
        useState<string | null>(null);

    const handleCopyPix = async () => {
        if (!pixCode) return;

        await navigator.clipboard.writeText(pixCode);

        alert("Código PIX copiado!");
    };

    const [cardHolderName, setCardHolderName] =
        useState("");

    const [cardNumber, setCardNumber] =
        useState("");

    const [cardExpiration, setCardExpiration] =
        useState("");

    const [cardCvv, setCardCvv] =
        useState("");

    const [totalParcelas, setTotalParcelas] = useState<number>(1);

    const handleConfirm = async () => {
        setIsLoading(true);

        setErrorMsg(null);

        setPixCode(null);

        setPixQrCodeImage(null);

        await handleCheckout(
            cartId,
            total,
            t,
            selectedMethod,

            {
                holderName: cardHolderName,
                number: cardNumber,
                expiration: cardExpiration,
                cvv: cardCvv,
                totalParcelas: totalParcelas
            },

            (order) => {
                /**
                 * =========================
                 * PIX
                 * =========================
                 */
                if (selectedMethod === "PIX") {
                    const pix = order?.pix;

                    if (pix) {
                        setPixCode(pix.pixCode);

                        setPixQrCodeImage(
                            pix.pixQRCodeImage,
                        );
                    }
                }

                if (selectedMethod !== "PIX") {
                    onSuccess();
                    onClose();
                }
            },

            (msg) => setErrorMsg(msg),
        );

        setIsLoading(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography fontWeight={700}>
                    Confirmar Pedido
                </Typography>

                <IconButton
                    size="small"
                    onClick={onClose}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {errorMsg && (
                        <Alert
                            severity="error"
                            onClose={() =>
                                setErrorMsg(null)
                            }
                        >
                            {errorMsg}
                        </Alert>
                    )}

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Forma de pagamento
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {PAYMENT_OPTIONS.map((option) => {
                            const isSelected =
                                selectedMethod ===
                                option.value;

                            return (
                                <Box
                                    key={option.value}
                                    onClick={() =>
                                        setSelectedMethod(
                                            option.value,
                                        )
                                    }
                                    sx={{
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border:
                                            "1.5px solid",
                                        borderColor:
                                            isSelected
                                                ? "primary.main"
                                                : "divider",
                                        bgcolor:
                                            isSelected
                                                ? "primary.50"
                                                : "background.paper",
                                        cursor: "pointer",
                                        transition:
                                            "border-color 0.15s, background-color 0.15s",
                                        "&:hover": {
                                            borderColor:
                                                "primary.light",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: isSelected
                                                ? "primary.main"
                                                : "text.secondary",
                                            display: "flex",
                                        }}
                                    >
                                        {option.icon}
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight={
                                                isSelected
                                                    ? 700
                                                    : 400
                                            }
                                        >
                                            {option.label}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {
                                                option.description
                                            }
                                        </Typography>
                                    </Box>

                                    {isSelected && (
                                        <Box
                                            sx={{
                                                ml: "auto",
                                                width: 10,
                                                height: 10,
                                                borderRadius:
                                                    "50%",
                                                bgcolor:
                                                    "primary.main",
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center",
                            p: 1.5,
                            bgcolor: "action.hover",
                            borderRadius: 1,
                        }}
                    >
                        {(selectedMethod === "CREDIT_CARD" ||
                            selectedMethod === "CREDIT_CARD_EARLY_BUYER" ||
                            selectedMethod === "DEBIT_CARD") && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="Nome no cartão"
                                    value={cardHolderName}
                                    onChange={(e) =>
                                        setCardHolderName(e.target.value)
                                    }
                                    fullWidth
                                />

                                <TextField
                                    label="Número do cartão"
                                    value={cardNumber}
                                    onChange={(e) =>
                                        setCardNumber(e.target.value)
                                    }
                                    fullWidth
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                    }}
                                >
                                    <TextField
                                        label="Validade"
                                        placeholder="10/2026"
                                        value={cardExpiration}
                                        onChange={(e) =>
                                            setCardExpiration(e.target.value)
                                        }
                                        fullWidth
                                    />

                                    <TextField
                                        label="CVV"
                                        value={cardCvv}
                                        onChange={(e) =>
                                            setCardCvv(e.target.value)
                                        }
                                        fullWidth
                                    />
                                    <TextField
                                        label="Total de Parcelas"
                                        value={totalParcelas}
                                        onChange={(e) =>
                                            setTotalParcelas(Number(e.target.value))
                                        }
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        )}
                        <Typography
                            variant="body1"
                            fontWeight={600}
                        >
                            Total
                        </Typography>

                        <Typography
                            variant="h6"
                            fontWeight={700}
                            color="success.main"
                        >
                            R${" "}
                            {(
                                total / 100
                            )?.toLocaleString(
                                "pt-BR",
                                {
                                    minimumFractionDigits: 2,
                                },
                            )}
                        </Typography>
                    </Box>

                    {pixQrCodeImage && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection:
                                    "column",
                                alignItems: "center",
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Typography
                                fontWeight={700}
                            >
                                Escaneie o QR Code
                            </Typography>

                            <Box
                                component="img"
                                src={pixQrCodeImage}
                                alt="PIX QR Code"
                                sx={{
                                    width: 240,
                                    height: 240,
                                    objectFit:
                                        "contain",
                                    borderRadius: 2,
                                    border:
                                        "1px solid",
                                    borderColor:
                                        "divider",
                                    p: 1,
                                    bgcolor:
                                        "background.paper",
                                }}
                            />

                            {pixCode && (
                                <Box
                                    sx={{
                                        width: "100%",
                                        display:
                                            "flex",
                                        flexDirection:
                                            "column",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={
                                            600
                                        }
                                    >
                                        Código PIX
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        value={
                                            pixCode
                                        }
                                        InputProps={{
                                            readOnly:
                                                true,
                                        }}
                                    />

                                    <PrimaryButton
                                        label="Copiar código PIX"
                                        onClickAction={
                                            handleCopyPix
                                        }
                                        icon={
                                            <ContentCopyIcon />
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <PrimaryButton
                    label={
                        isLoading
                            ? "Processando..."
                            : "Ir para pagamento"
                    }
                    onClickAction={handleConfirm}
                    isLoading={isLoading}
                />
            </DialogActions>
        </Dialog>
    );
};

export default CheckoutDialog;